import React, { useState, useEffect, useRef } from 'react';
import { GlobeAltIcon, ChatBubbleBottomCenterTextIcon, SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import FileUpload from './FileUpload';
import { useUser } from '../userContext';
import { auth } from '../firebase';
import { deriveBrandAccentPalette, generateCustomTheme, PRESET_DEFINITIONS } from '../campaign/stylePresets';
import { generateFullTheme, applyFullThemeToOptions } from '../services/themeGenerator';

// Normalise a URL that might be missing a scheme so fetch() doesn't choke.
function normaliseUrl(raw) {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

const LOGO_TOOLTIP = '200×200px recommended. Square images work best. Shown as the collectible your players chase in-game.';

// gameType presets ship with a default Star asset so games are playable
// out of the box. In the wizard's brand step we treat that default as
// "no logo set" so users see a clean upload prompt - if they skip, the
// preset stays in options and the game still has a collectible to render.
const DEFAULT_PRESET_LOGOS = new Set([
    '/assets/library/items/new-svg/star.svg',
]);

/**
 * WizardBrand — combined AI "Brand it" step replacing the separate
 * colours and logo wizard steps. Calls /extract-brand then /generate-full-theme
 * (via the shared themeGenerator helper), builds a full customColors + theme
 * + assets update, and calls onComplete when done.
 *
 * Props:
 *   gameData        — current campaign document
 *   handleOptionsUpdate(updates) — merges updates into options
 *   onComplete()    — advances to the next step (or finishes wizard)
 *   logoConfig      — LOGO_CONFIG entry: { field, nameKey, title, body } | null
 */
export default function WizardBrand({ gameData, handleOptionsUpdate, onComplete, logoConfig, controllerRef, onStateChange }) {
    const { businessProfile } = useUser();

    const [url, setUrl] = useState('');
    const [prompt, setPrompt] = useState('');
    const [mode, setMode] = useState('website'); // 'website' | 'description'
    // Phase drives the whole UI: the form, the loading takeover, or the
    // post-generation review where the user can tweak before continuing.
    const [phase, setPhase] = useState('form'); // 'form' | 'reading' | 'designing' | 'review'
    const [error, setError] = useState(null);
    // Snapshot of the last generated theme + brand updates so we can pass
    // it to onComplete when the user confirms in the review step (avoids
    // losing the just-applied options to a state-flush race).
    const lastResultRef = useRef(null);

    const options = gameData?.options || {};
    const gameType = gameData?.gameType || null;

    const logoField = logoConfig?.field || 'collectibleImage';
    const logoNameKey = logoConfig?.nameKey || 'collectibleImageName';
    const logoDisplayText = logoConfig?.title || 'Your logo';
    const logoSubtitle = logoConfig?.body || 'Square images work best.';

    // Hide the default preset (Star etc.) so the upload tile reads as empty
    // until the user actively picks/uploads a logo. The real options keep
    // the preset so skipping still leaves a collectible in-game.
    const userLogoUrl = options[logoField];
    const userHasLogo = !!userLogoUrl && !DEFAULT_PRESET_LOGOS.has(userLogoUrl);
    const displayOptions = userHasLogo
        ? options
        : { ...options, [logoField]: null, [logoNameKey]: null };

    // Whichever mode is active is the one that counts for "do we have anything
    // to generate from?" The other field is hidden so its value is ignored.
    const activeInputFilled = mode === 'website' ? !!url.trim() : !!prompt.trim();
    const allEmpty = !activeInputFilled && !userHasLogo;
    const isLoading = phase === 'reading' || phase === 'designing';
    const inReview = phase === 'review';

    const handleGenerate = async () => {
        if (isLoading) return;
        setError(null);

        let brandContext = null;
        // Aggregate brand-derived option updates so they can be passed
        // alongside the theme update to onComplete - this avoids the
        // saveCurrentStep race when state hasn't flushed.
        const brandOptionUpdates = {};

        // Step 1: extract brand from URL if the user is in website mode AND
        // has typed one. In description mode the URL field isn't shown, so
        // any stale value from a mode toggle is ignored.
        if (mode === 'website' && url.trim()) {
            setPhase('reading');
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/app/extract-brand`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ url: normaliseUrl(url) }),
                    }
                );
                if (!res.ok) {
                    const body = await res.json().catch(() => null);
                    setError(
                        body?.error
                            ? `We couldn't read that site: ${body.error}. Want to try again or skip?`
                            : "We couldn't reach that site. Want to try again or skip?"
                    );
                    setPhase('form');
                    return;
                }
                const data = await res.json();
                brandContext = {
                    title: data.title || null,
                    description: data.description || null,
                    vibe: data.vibe || null,
                    palette: data.palette || null,
                };

                // Pre-populate logo, title, and intro copy from the extracted
                // brand. The user is explicitly running website extraction —
                // a previous generation's logo gets replaced here, otherwise
                // changing the URL would feel broken (everything else
                // updates except the logo).
                if (data.logoUrl && logoConfig) {
                    brandOptionUpdates[logoField] = data.logoUrl;
                    brandOptionUpdates[logoNameKey] = null;
                }
                // Truncate cleanly: cut at the last word boundary inside the
                // limit and append an ellipsis so the result never reads as
                // a mid-word cut ("…for logisti").
                const cleanCut = (raw, max) => {
                    const s = String(raw);
                    if (s.length <= max) return s;
                    const slice = s.slice(0, max);
                    const lastSpace = slice.lastIndexOf(' ');
                    const trimmed = (lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd();
                    return trimmed.replace(/[.,;:!?]+$/, '') + '…';
                };
                if (data.title) brandOptionUpdates.title = cleanCut(data.title, 80);
                if (data.description) brandOptionUpdates.intro = cleanCut(data.description, 160);
                if (Object.keys(brandOptionUpdates).length) handleOptionsUpdate(brandOptionUpdates);
            } catch (err) {
                console.error('[WizardBrand] extract-brand error:', err);
                setError("We couldn't reach that site. Want to try again or skip?");
                setPhase('form');
                return;
            }
        }

        // Step 2: generate theme
        setPhase('designing');
        try {
            // /generate-full-theme picks colours, fonts, frame/overlay/layout/motion
            // AND asset IDs (background, character, platform, pipe, obstacle,
            // collectible, snake skin) appropriate for the game type — so the
            // generated theme runs through to the in-game art, not just the
            // start card.
            const theme = await generateFullTheme({
                gameType,
                prompt: mode === 'description' ? prompt.trim() : undefined,
                brand: brandContext || undefined,
            });

            const themeUpdate = applyFullThemeToOptions(theme, gameType);
            handleOptionsUpdate(themeUpdate);

            // Stash the combined snapshot for the Continue handler — same
            // shape we used to pass straight to onComplete. Then drop into
            // the review screen so the user can sanity-check / tweak before
            // we navigate away.
            lastResultRef.current = { ...brandOptionUpdates, ...themeUpdate };
            setPhase('review');
        } catch (err) {
            console.error('[WizardBrand] generate-full-theme error:', err);
            setError(err.message || 'Theme generation failed. Try again or skip.');
            setPhase('form');
        }
    };

    // Continue button on the review screen — propagate the same snapshot
    // we've already merged into options up to the wizard.
    const handleConfirm = () => {
        const snapshot = lastResultRef.current || {};
        onComplete(snapshot);
    };

    // Regenerate from the review screen: wipe local result and return to
    // the form with the same input still typed in.
    const handleRegenerate = () => {
        lastResultRef.current = null;
        setError(null);
        setPhase('form');
    };

    // Inline edits on the review screen: mirror straight into options so
    // the next save / preview navigation sees them.
    const updateField = (field, value) => {
        handleOptionsUpdate({ [field]: value });
        if (lastResultRef.current) lastResultRef.current[field] = value;
    };

    const handleSkip = () => {
        // Skip applies the SmilesFlow Default preset (same one the AI uses
        // as its fallback), so a skipped game and an AI-default game render
        // identically. The preset values come from PRESET_DEFINITIONS, which
        // is the same source the user-facing theme selector uses.
        const preset = PRESET_DEFINITIONS['preset-smilesflow-default'];
        const fullPalette = deriveBrandAccentPalette(
            preset.colors.background,
            preset.colors.primaryAction,
            preset.colors.text
        );
        const newTheme = generateCustomTheme(fullPalette, preset.font, gameType);
        const themeUpdate = {
            customColors: { ...fullPalette, headingText: '' },
            theme: newTheme,
            themeCustomised: false,
            selectedThemeName: preset.name,
            selectedThemeCategory: null,
            advancedColorsManual: false,
            frameStyle: preset.style.frameStyle,
            cardOverlayEffect: preset.style.overlay,
            backgroundPattern: preset.style.pattern,
            cardLayout: preset.style.layout,
            motionStyle: preset.style.motionStyle,
        };
        handleOptionsUpdate(themeUpdate);
        onComplete(themeUpdate);
    };

    // Expose Generate / Skip / Confirm / Regenerate to the wizard footer.
    // The ref captures the latest closure on every render so the footer
    // always calls into the current handlers.
    useEffect(() => {
        if (controllerRef) {
            controllerRef.current = {
                generate: handleGenerate,
                skip: handleSkip,
                confirm: handleConfirm,
                regenerate: handleRegenerate,
            };
        }
    });

    useEffect(() => {
        if (onStateChange) {
            onStateChange({
                canGenerate: !allEmpty,
                isLoading,
                inReview,
                phase,
            });
        }
    }, [allEmpty, isLoading, inReview, phase, onStateChange]);

    // ── LOADING TAKEOVER ──
    if (isLoading) {
        const headline = phase === 'reading' ? 'Reading your site' : 'Designing your theme';
        const subtext = phase === 'reading'
            ? 'Pulling colours, logo, and vibe from your website.'
            : 'Picking a preset, mixing your palette, and writing the copy.';
        return (
            <div className="flex flex-col items-center justify-center text-center py-16 sm:py-24">
                <div className="relative w-16 h-16 mb-6">
                    <div className="absolute inset-0 rounded-full border-2 border-[var(--admin-accent)]/20" />
                    <div className="absolute inset-0 rounded-full border-2 border-[var(--admin-accent)] border-t-transparent animate-spin" />
                    <SparklesIcon className="absolute inset-0 m-auto w-7 h-7 text-[var(--admin-accent)]" />
                </div>
                <h2 className="text-xl gch-heading gch-text-primary mb-2">{headline}…</h2>
                <p className="text-sm gch-text-secondary max-w-xs">{subtext}</p>
                {mode === 'website' && url.trim() && (
                    <p className="text-xs gch-text-muted mt-4 truncate max-w-xs">{normaliseUrl(url)}</p>
                )}
            </div>
        );
    }

    // ── REVIEW: show what we generated, let user edit before continuing ──
    if (inReview) {
        const palette = options.customColors || {};
        const swatches = [
            { hex: palette.background, label: 'Background' },
            { hex: palette.primary, label: 'Primary' },
            { hex: palette.primaryAction, label: 'Action' },
        ].filter(s => /^#[0-9A-F]{6}$/i.test(s.hex || ''));
        const logoUrl = options[logoField];
        const showLogo = !!logoUrl && !DEFAULT_PRESET_LOGOS.has(logoUrl);

        return (
            <div className="space-y-5">
                <div className="rounded-xl border border-[var(--admin-border)] bg-white/5 p-4 space-y-4">
                    <div className="flex items-start gap-4">
                        {showLogo && (
                            <img
                                src={logoUrl}
                                alt="Brand logo"
                                className="w-16 h-16 rounded-lg object-contain bg-white/10 border border-[var(--admin-border)] shrink-0"
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--admin-accent)] mb-1">Here's what we found</p>
                            <p className="text-sm gch-text-secondary leading-snug">Quick tweak now, or change anything (colours, logo, layout, copy) later in the full editor.</p>
                        </div>
                    </div>

                    {swatches.length > 0 && (
                        <div className="flex items-center gap-2">
                            {swatches.map((s) => (
                                <div key={s.label} className="flex items-center gap-1.5">
                                    <span
                                        className="w-6 h-6 rounded-md border border-white/15 shadow-sm"
                                        style={{ backgroundColor: s.hex }}
                                        title={`${s.label}: ${s.hex}`}
                                    />
                                </div>
                            ))}
                            <span className="text-[11px] gch-text-muted ml-1">Brand palette</span>
                        </div>
                    )}
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold gch-text-primary" htmlFor="review-title">Game title</label>
                    <input
                        id="review-title"
                        type="text"
                        value={options.title || ''}
                        onChange={(e) => updateField('title', e.target.value.slice(0, 40))}
                        className="w-full rounded-lg border border-[var(--admin-border)] bg-white/5 px-3 py-2.5 text-sm gch-text-primary focus:outline-none focus:border-[var(--admin-accent)] focus:ring-1 focus:ring-[var(--admin-accent)] transition-colors"
                    />
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold gch-text-primary" htmlFor="review-intro">Intro</label>
                    <textarea
                        id="review-intro"
                        rows={3}
                        value={options.intro || ''}
                        onChange={(e) => updateField('intro', e.target.value.slice(0, 140))}
                        className="w-full rounded-lg border border-[var(--admin-border)] bg-white/5 px-3 py-2.5 text-sm gch-text-primary focus:outline-none focus:border-[var(--admin-accent)] focus:ring-1 focus:ring-[var(--admin-accent)] transition-colors resize-none"
                    />
                    <p className="text-[11px] gch-text-muted">{(options.intro || '').length}/140</p>
                </div>

                <button
                    type="button"
                    onClick={handleRegenerate}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold gch-text-secondary hover:gch-text-primary transition-colors"
                >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Try a different URL or description
                </button>
            </div>
        );
    }

    // ── FORM (default) ──
    return (
        <div className="space-y-6">
            {/* Mode toggle: pull brand from a website OR from a free-form
                description. Only one input shown at a time so users don't
                wonder which one wins. */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg border border-[var(--admin-border)] bg-white/5">
                <button
                    type="button"
                    onClick={() => setMode('website')}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                        mode === 'website'
                            ? 'bg-[var(--admin-accent)] text-[var(--admin-bg-surface)]'
                            : 'gch-text-secondary hover:gch-text-primary'
                    }`}
                >
                    <GlobeAltIcon className="w-3.5 h-3.5" />
                    From website
                </button>
                <button
                    type="button"
                    onClick={() => setMode('description')}
                    disabled={isLoading}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                        mode === 'description'
                            ? 'bg-[var(--admin-accent)] text-[var(--admin-bg-surface)]'
                            : 'gch-text-secondary hover:gch-text-primary'
                    }`}
                >
                    <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5" />
                    From description
                </button>
            </div>

            {mode === 'website' ? (
                <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs font-semibold gch-text-primary" htmlFor="brand-url">
                        <GlobeAltIcon className="w-3.5 h-3.5 text-[var(--admin-accent)]" />
                        Website URL
                    </label>
                    <input
                        id="brand-url"
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="yourbusiness.com"
                        disabled={isLoading}
                        className="w-full rounded-lg border border-[var(--admin-border)] bg-white/5 px-3 py-2.5 text-sm gch-text-primary placeholder:gch-text-muted focus:outline-none focus:border-[var(--admin-accent)] focus:ring-1 focus:ring-[var(--admin-accent)] disabled:opacity-50 transition-colors"
                    />
                    <p className="text-[11px] gch-text-muted">We'll pull your brand colours, logo, and vibe from here.</p>
                </div>
            ) : (
                <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs font-semibold gch-text-primary" htmlFor="brand-prompt">
                        <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5 text-[var(--admin-accent)]" />
                        Describe the brand
                    </label>
                    <textarea
                        id="brand-prompt"
                        rows={3}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="e.g. retro arcade coffee shop, bold neon, premium minimalist…"
                        disabled={isLoading}
                        className="w-full rounded-lg border border-[var(--admin-border)] bg-white/5 px-3 py-2.5 text-sm gch-text-primary placeholder:gch-text-muted focus:outline-none focus:border-[var(--admin-accent)] focus:ring-1 focus:ring-[var(--admin-accent)] disabled:opacity-50 transition-colors resize-none"
                    />
                    <p className="text-[11px] gch-text-muted">Name, vibe, or anything that defines the brand.</p>
                </div>
            )}

            {/* Logo upload — only shown in 'description' mode. In 'website'
                mode the brand extractor pulls the logo from the site's meta
                tags, so the upload tile would be redundant noise. */}
            {logoConfig && mode === 'description' && (
                <FileUpload
                    displayText={logoDisplayText}
                    subtitle={logoSubtitle}
                    tooltip={LOGO_TOOLTIP}
                    field={logoField}
                    selectionNameKey={logoNameKey}
                    options={displayOptions}
                    onSuccess={handleOptionsUpdate}
                    onDelete={handleOptionsUpdate}
                    displayPreview
                    maxDimensions={{ width: 240, height: 240 }}
                    compact
                    showLibrary={false}
                    companyId={businessProfile?.id}
                />
            )}

            {/* Error */}
            {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-400 leading-snug">
                    {error}
                </div>
            )}

        </div>
    );
}
